import { memo, useCallback } from 'react';
import { Box, FormControl, MenuItem, Select, Typography } from '@mui/material';
import { QueryField, QueryRule } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import ValueEditor from '../ValueEditors';
import CheckboxEditor from '../ValueEditors/CheckboxEditor';
import SelectionDialogEditor from '../ValueEditors/SelectionDialogEditor';
import DualListSelectionDialogEditor from '../ValueEditors/DualListSelectionDialogEditor';
import StoreListImport from '../StoreListImport';
import PurchasePeriodQuickSelect from '../PurchasePeriodQuickSelect';
import {
    COUNTER_FIELD_NAME,
    DIALOG_SELECT_FIELD_NAMES,
    DUAL_LIST_FIELD_NAMES,
    DUAL_LIST_NO_SEARCH_FIELD_NAMES,
    PURCHASE_PERIOD_FIELD_NAME,
    ROW_SPAN_2_FIELD_NAMES,
    STORE_FIELD_NAME,
    resolveFieldLabel,
    resolveFieldValues,
} from './fieldConfig';

interface FieldRowProps {
    field: QueryField;
    rule: QueryRule | undefined;
    onUpdateField: (fieldName: string, data: Partial<QueryRule>) => void;
    pairedField?: QueryField;
    pairedRule?: QueryRule;
    pairedIsOptinStatus: boolean;
}

const rowSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 1,
    p: 1.5,
    bgcolor: '#ffffff',
    borderRadius: 1,
    border: '1px solid #e0e0e0',
    '&:hover': { borderColor: '#556ee6', boxShadow: '0 2px 8px rgba(85, 110, 230, 0.1)' },
};

function FieldLabel({ field }: { field: QueryField }) {
    const { t } = useLanguage();
    return (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#495057' }}>
            {resolveFieldLabel(field, t)}
        </Typography>
    );
}

function OptinStatusRow({ field, rule, pairedField, pairedRule, onUpdateField }: {
    field: QueryField;
    rule: QueryRule | undefined;
    pairedField: QueryField;
    pairedRule: QueryRule | undefined;
    onUpdateField: (fieldName: string, data: Partial<QueryRule>) => void;
}) {
    const { t } = useLanguage();
    const handleValueChange = useCallback(
        (value: QueryRule['value']) => onUpdateField(field.name, { value }),
        [field.name, onUpdateField]
    );
    const handlePairedValueChange = useCallback(
        (value: QueryRule['value']) => onUpdateField(pairedField.name, { value }),
        [pairedField.name, onUpdateField]
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FieldLabel field={field} />
                <ValueEditor
                    ruleId={rule?.id || field.name}
                    field={field}
                    operator={rule?.operator || field.defaultOperator || '='}
                    value={rule?.value}
                    onChange={handleValueChange}
                    compact
                />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1.5, borderTop: '1px solid #e0e0e0' }}>
                <FieldLabel field={pairedField} />
                <CheckboxEditor
                    ruleId={pairedRule?.id || pairedField.name}
                    value={pairedRule?.value ?? null}
                    values={resolveFieldValues(pairedField, t) || []}
                    onChange={handlePairedValueChange}
                    compact
                />
            </Box>
        </Box>
    );
}

function DualListRow({ field, rule, onUpdateField }: {
    field: QueryField;
    rule: QueryRule | undefined;
    onUpdateField: (fieldName: string, data: Partial<QueryRule>) => void;
}) {
    const { t } = useLanguage();
    const handleChange = useCallback(
        (data: { value: string[]; notValue: string[] }) => onUpdateField(field.name, { operator: 'in', ...data }),
        [field.name, onUpdateField]
    );

    const hideSearch = DUAL_LIST_NO_SEARCH_FIELD_NAMES.has(field.name);
    const outerLabels = hideSearch
        ? {
            include: t('pages.clients.queryBuilder.simple.dualSelectionDialog.belongsToTypes'),
            exclude: t('pages.clients.queryBuilder.simple.dualSelectionDialog.doesNotBelongToTypes'),
        }
        : undefined;

    return (
        <>
            <FieldLabel field={field} />
            <DualListSelectionDialogEditor
                ruleId={rule?.id || field.name}
                includeValues={rule && Array.isArray(rule.value) ? rule.value.map(String) : []}
                excludeValues={rule?.notValue ?? []}
                values={resolveFieldValues(field, t) || []}
                title={resolveFieldLabel(field, t)}
                onChange={handleChange}
                hideSearch={hideSearch}
                outerLabels={outerLabels}
                dialogMaxWidth={hideSearch ? 'sm' : 'md'}
            />
        </>
    );
}

function DefaultRow({ field, rule, pairedField, pairedRule, onUpdateField }: {
    field: QueryField;
    rule: QueryRule | undefined;
    pairedField: QueryField | undefined;
    pairedRule: QueryRule | undefined;
    onUpdateField: (fieldName: string, data: Partial<QueryRule>) => void;
}) {
    const { t } = useLanguage();

    const handleValueChange = useCallback(
        (value: QueryRule['value']) => onUpdateField(field.name, { value }),
        [field.name, onUpdateField]
    );
    const handlePairedValueChange = useCallback(
        (value: QueryRule['value']) => pairedField && onUpdateField(pairedField.name, { value }),
        [pairedField, onUpdateField]
    );
    const handleApplyPeriod = useCallback(
        (value: string) => onUpdateField(field.name, { operator: 'between', value }),
        [field.name, onUpdateField]
    );

    return (
        <>
            <FieldLabel field={field} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                <Box sx={pairedField ? { flex: '0 1 260px', minWidth: '220px' } : { flexGrow: 1, minWidth: '200px', alignSelf: 'stretch' }}>
                    {DIALOG_SELECT_FIELD_NAMES.has(field.name) ? (
                        <SelectionDialogEditor
                            ruleId={rule?.id || field.name}
                            value={rule?.value}
                            values={resolveFieldValues(field, t) || []}
                            title={resolveFieldLabel(field, t)}
                            onChange={handleValueChange}
                        />
                    ) : (
                        <ValueEditor
                            ruleId={rule?.id || field.name}
                            field={field.values ? { ...field, values: resolveFieldValues(field, t) } : field}
                            operator={rule?.operator || field.defaultOperator || '='}
                            value={rule?.value}
                            onChange={handleValueChange}
                            compact
                            betweenSwapIfInverted={field.name === COUNTER_FIELD_NAME}
                        />
                    )}
                </Box>
                {pairedField && (
                    (pairedField.values?.length ?? 0) > 1 ? (
                        <FormControl size="small" sx={{ minWidth: 100, flexShrink: 0 }}>
                            <Select
                                value={typeof pairedRule?.value === 'string' ? pairedRule.value : ''}
                                onChange={(e) => handlePairedValueChange(e.target.value)}
                                displayEmpty
                            >
                                <MenuItem value="" disabled>
                                    <em>{t('pages.clients.queryBuilder.selectPlaceholder')}</em>
                                </MenuItem>
                                {pairedField.values!.map(v => (
                                    <MenuItem key={v.name} value={v.name}>{v.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (pairedField.values?.length ?? 0) === 1 ? (
                        <Typography variant="body2" sx={{ color: '#495057' }}>
                            {pairedField.values![0].label}
                        </Typography>
                    ) : null
                )}
                {field.name === STORE_FIELD_NAME && (
                    <StoreListImport values={field.values || []} selectedValue={rule?.value} onChange={handleValueChange} />
                )}
                {field.name === PURCHASE_PERIOD_FIELD_NAME && (
                    <PurchasePeriodQuickSelect onApply={handleApplyPeriod} />
                )}
            </Box>
        </>
    );
}

const FieldRow = memo(function FieldRow({ field, rule, onUpdateField, pairedField, pairedRule, pairedIsOptinStatus }: FieldRowProps) {
    return (
        <Box
            sx={{
                ...rowSx,
                gridRow: ROW_SPAN_2_FIELD_NAMES.has(field.name) ? 'span 2' : undefined,
                alignSelf: ROW_SPAN_2_FIELD_NAMES.has(field.name) ? 'stretch' : undefined,
            }}
        >
            {pairedField && pairedIsOptinStatus ? (
                <OptinStatusRow field={field} rule={rule} pairedField={pairedField} pairedRule={pairedRule} onUpdateField={onUpdateField} />
            ) : DUAL_LIST_FIELD_NAMES.has(field.name) ? (
                <DualListRow field={field} rule={rule} onUpdateField={onUpdateField} />
            ) : (
                <DefaultRow field={field} rule={rule} pairedField={pairedField} pairedRule={pairedRule} onUpdateField={onUpdateField} />
            )}
        </Box>
    );
});

export default FieldRow;
